import React from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { fetchInstructorDashboard } from "../../api/backend";
import { fetchClassCoiReport } from "../../api/apiClient";
import { TenantContext } from "../../contexts/TenantContext";
import { parseLocalDate } from "../../utils/dateUtils";

const D = {
  primary50:  "color-mix(in srgb, var(--color-primary) 8%, transparent)",
  ink:   "var(--color-ink)",
  ink2:  "rgba(31,22,51,0.75)",
  ink3:  "rgba(31,22,51,0.6)",
  ink4:  "rgba(31,22,51,0.5)",
  line:   "var(--color-hairline-cloud)",
  line2:  "var(--color-hairline-cloud)",
  surface:   "var(--color-surface-canvas-light)",
  surface2:  "var(--color-surface-press-light)",
  surface3:  "var(--color-surface-press-stronger)",
  green:     "var(--color-success)",
  green50:   "var(--color-success-bg)",
  green100:  "var(--color-success-bg)",
  amber:     "var(--color-warn)",
  amber50:   "var(--color-warn-bg)",
  amber100:  "var(--color-warn-bg)",
  live:      "#c23d68",
  live50:    "color-mix(in srgb, var(--color-accent-pink) 10%, transparent)",
  live100:   "color-mix(in srgb, var(--color-accent-pink) 22%, transparent)",
  danger:    "var(--color-danger)",
  danger50:  "var(--color-danger-bg)",
  shadowSm:  "var(--shadow-card-sm)",
  primary: "var(--color-primary)",
  primary10: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
  primary700: "color-mix(in srgb, var(--color-primary) 85%, black)",
};

function useD() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return {
    ...D,
    primary,
    primary700: theme.palette.primary.dark,
    primary10: alpha(primary, 0.06),
  };
}

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const REPORTS = [
  { id: "attendance", title: "Attendance Summary", description: "Course and session attendance — present, tardy, no-show and call-out breakdowns by student.", lastGenerated: null, formats: ["PDF", "CSV"], variant: "primary" },
  { id: "program", title: "Program-Wide Data", description: "Aggregate class performance. Surface the concepts students struggle with across the cohort.", lastGenerated: null, formats: ["CSV", "XLSX"], variant: "purple" },
  { id: "skills", title: "Skills Proficiency", description: "Drill into exam and practical evaluation results by skill checklist and individual student.", lastGenerated: null, formats: ["PDF", "XLSX"], variant: "primary" },
  { id: "instructor", title: "Instructor Efficiency", description: "Comparative outcome analysis across instructors — sign-off rates and student outcomes.", lastGenerated: null, formats: ["PDF"], variant: "purple" },
  { id: "portfolio", title: "Student Portfolio", description: "Consolidated student progress — grades, skill completions and certificate readiness.", lastGenerated: null, formats: ["PDF"], variant: "green" },
  { id: "pipeline", title: "Certification Pipeline", description: "Track which students are on track for certification completion and graduation readiness.", lastGenerated: null, formats: ["PDF"], variant: "green" },
  { id: "incomplete", title: "Incomplete Modules", description: "Identify at-risk students who have not completed required skill checklists or scenarios.", lastGenerated: null, formats: ["CSV"], variant: "amber" },
  { id: "coi_compliance", title: "Insurance (COI) Compliance", description: "Certificate of Insurance on file per student at registration. Flag anyone without a valid COI.", lastGenerated: null, formats: ["PDF"], variant: "live" },
  { id: "archive", title: "Historical Archive", description: "View and export data from previous semesters and completed course sessions.", lastGenerated: null, formats: ["PDF", "CSV"], variant: "blue" },
];

const SECTIONS = [
  { label: "Performance & attendance", reportIds: ["attendance", "program", "skills", "instructor"] },
  { label: "Students & certification", reportIds: ["portfolio", "pipeline", "incomplete"] },
  { label: "Compliance & archive", reportIds: ["coi_compliance", "archive"] },
];

function makeVariantStyles(D) {
  return {
    primary: { bg: D.primary10, color: D.primary },
    green:   { bg: D.green50,   color: D.green },
    amber:   { bg: D.amber50,   color: D.amber },
    blue:    { bg: "rgba(37,99,235,.10)", color: "var(--color-primary)" },
    purple:  { bg: "rgba(107,79,187,.10)", color: "var(--color-accent-violet)" },
    live:    { bg: D.live50,    color: D.live },
  };
}

function SvgIcon({ size = 15, children }) {
  return (
    <Box component="svg" width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </Box>
  );
}

function RstatTile({ value, label, sub, subColor }) {
  const D = useD();
  return (
    <Box sx={{ bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${D.line}`, borderRadius: "14px",
      p: "18px", boxShadow: D.shadowSm }}>
      <Box sx={{ fontSize: "30px", fontWeight: 800, letterSpacing: "-.04em", color: D.ink, lineHeight: 1, fontFamily: "var(--font-display)" }}>
        {value}
      </Box>
      <Box sx={{ fontSize: "12px", fontWeight: 600, color: D.ink3, mt: "4px" }}>{label}</Box>
      {sub && (
        <Box sx={{ fontSize: "11.5px", fontWeight: 600, color: subColor || D.ink3, mt: "4px" }}>{sub}</Box>
      )}
    </Box>
  );
}

function PanelCard({ iconBg, iconColor, icon, title, headerRight, children }) {
  const D = useD();
  return (
    <Box sx={{ bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${D.line}`, borderRadius: "16px",
      boxShadow: D.shadowSm, overflow: "hidden", mb: "16px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        px: "20px", py: "13px", borderBottom: `1px solid ${D.line2}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
          <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: iconBg || D.primary10,
            color: iconColor || D.primary, display: "grid", placeItems: "center" }}>
            {icon}
          </Box>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, letterSpacing: "-.01em", color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>
            {title}
          </Typography>
        </Box>
        {headerRight}
      </Box>
      <Box sx={{ p: "20px" }}>{children}</Box>
    </Box>
  );
}

function StatusPill({ status }) {
  const D = useD();
  const cfg =
    status === "completed" ? { bg: D.primary10, color: D.primary, dot: D.primary, label: "Completed" }
    : status === "on-track" ? { bg: D.green50, color: D.green, dot: D.green, label: "On Track" }
    : status === "behind" ? { bg: D.amber50, color: D.amber, dot: D.amber, label: "Behind" }
    : { bg: D.surface3, color: D.ink3, dot: D.ink4, label: "Not Started" };
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px",
      fontSize: "11px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
      px: "9px", py: "3px", borderRadius: "999px", bgcolor: cfg.bg, color: cfg.color, whiteSpace: "nowrap" }}>
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </Box>
  );
}

function Ava({ initials, color, size = 28 }) {
  return (
    <Box sx={{ width: size, height: size, borderRadius: "50%", bgcolor: color,
      display: "grid", placeItems: "center", fontSize: size < 30 ? "10px" : "12px",
      fontWeight: 800, color: "var(--color-on-primary)", flexShrink: 0 }}>
      {initials}
    </Box>
  );
}

const AVA_COLORS = ["#c23d68","var(--color-primary)","var(--color-success)","var(--color-warn)","var(--color-primary)","var(--color-accent-violet)","var(--color-accent-violet)","#9333EA"];

function getInitials(name = "") {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.slice(0,2).toUpperCase();
}

function colorFor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (name.charCodeAt(i) + ((h << 5) - h)) | 0;
  return AVA_COLORS[Math.abs(h) % AVA_COLORS.length];
}

function formatDateShort(dateStr) {
  if (!dateStr) return null;
  const d = parseLocalDate(dateStr);
  if (!d || isNaN(d)) return null;
  return `${MONTH_ABBR[d.getMonth()]} ${d.getDate()}`;
}

function CoiStatusChip({ status }) {
  const map = {
    active: { label: "Active", color: "success" },
    expiring_soon: { label: "Expiring Soon", color: "warning" },
    expired: { label: "Expired", color: "error" },
  };
  const cfg = map[status] || { label: status || "Unknown", color: "default" };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

export default function CoordinatorReportsTab({ course, sessions = [], checklists = [], enrolledCount = 0 }) {
  const D = useD();
  const VARIANT_STYLES = makeVariantStyles(D);
  const { id } = useParams();
  const { tenant } = React.useContext(TenantContext);
  const [dashboard, setDashboard] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [generating, setGenerating] = React.useState({});
  const [generated, setGenerated] = React.useState({});
  const [coiRows, setCoiRows] = React.useState(null);
  const [coiLoading, setCoiLoading] = React.useState(false);
  const [coiError, setCoiError] = React.useState("");

  React.useEffect(() => {
    setLoading(true);
    fetchInstructorDashboard(id)
      .then((data) => { setDashboard(data); setLoading(false); })
      .catch(() => { setDashboard(null); setLoading(false); });
  }, [id]);

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress sx={{ color: D.primary }} />
    </Box>
  );

  // ── Stats ──────────────────────────────────────────────────────────────────
  const now = new Date();
  const completedSessions = sessions.filter((s) => {
    const end = s.endDateTime || s.end || s.date;
    return end && new Date(end) < now;
  });
  const futureSessions = sessions.filter((s) => {
    const end = s.endDateTime || s.end || s.date;
    return !end || new Date(end) >= now;
  });

  const avgAtt = (() => {
    const done = sessions.filter((s) => s.checkedIn > 0);
    if (!done.length) return 0;
    return Math.round(done.reduce((sum, s) => sum + (s.checkedIn / (s.totalStudents || 1)) * 100, 0) / done.length);
  })();

  const skillComp = checklists.length > 0
    ? Math.round(checklists.reduce((sum, c) => sum + (c.instructorCompletion || 0), 0) / checklists.length)
    : 0;

  // ── Per-student data ───────────────────────────────────────────────────────
  const registrations = Array.isArray(dashboard?.registrations) ? dashboard.registrations : [];
  const attendanceMap = {};
  if (Array.isArray(dashboard?.sessionAttendance)) {
    dashboard.sessionAttendance.forEach((sa) => {
      const uid = String(sa.userId || sa.studentId);
      if (!attendanceMap[uid]) attendanceMap[uid] = 0;
      if (sa.attended || sa.status === "Present" || sa.checkedInAt) attendanceMap[uid]++;
    });
  }


  const studentRows = registrations.map((r) => {
    const name = r.studentName || r.fullName || r.name || r.username || "Student";
    const uid = String(r.userId || r.studentId || r.id);
    const attended = attendanceMap[uid] || 0;
    const attPct = completedSessions.length > 0 ? Math.round((attended / completedSessions.length) * 100) : 0;
    const status = r.status === "CANCELLED" ? "cancelled"
      : attended === 0 && completedSessions.length > 0 ? "not-started"
      : attPct >= 80 ? "on-track"
      : attPct < 50 ? "behind"
      : "on-track";
    return { name, uid, attended, completedSessions: completedSessions.length, attPct, status, email: r.email || r.studentEmail || "" };
  });

  const handleGenerate = (reportId) => {
    if (reportId === "coi_compliance") {
      const tenantId = tenant?.id || tenant?.tenantId;
      if (!id) return;
      setCoiLoading(true);
      setCoiError("");
      fetchClassCoiReport(tenantId, id)
        .then((data) => setCoiRows(Array.isArray(data) ? data : []))
        .catch(() => setCoiError("Failed to load COI report."))
        .finally(() => setCoiLoading(false));
      return;
    }
    setGenerating((prev) => ({ ...prev, [reportId]: true }));
    setTimeout(() => {
      setGenerating((prev) => ({ ...prev, [reportId]: false }));
      setGenerated((prev) => ({ ...prev, [reportId]: true }));
    }, 1500);
  };

  function ReportIcon({ reportId }) {
    const icons = {
      attendance: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
      program: <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
      skills: <><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></>,
      instructor: <><path d="M12 20a8 8 0 1 0-8-8"/><polyline points="12 12 16 9"/><line x1="2" y1="12" x2="5" y2="12"/></>,
      portfolio: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
      pipeline: <><path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/><path d="m8.5 11-2 6 3-1.5L12 17l2.5-1.5L17 17l-2-6"/></>,
      incomplete: <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
      coi_compliance: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
      archive: <><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><polyline points="12 7 12 12 15 15"/></>,
    };
    const paths = icons[reportId] || icons.attendance;
    return (
      <Box component="svg" width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {paths}
      </Box>
    );
  }

  function ReportCard({ report }) {
    const isGenerating = generating[report.id];
    const isDone = generated[report.id];
    const isCoiReport = report.id === "coi_compliance";
    const v = VARIANT_STYLES[report.variant] || VARIANT_STYLES.primary;
    return (
      <Paper elevation={0} sx={{ display: "flex", flexDirection: "column", bgcolor: D.surface, border: `1px solid ${D.line}`, borderRadius: "15px", p: "18px", boxShadow: D.shadowSm, transition: "box-shadow .15s, border-color .15s, transform .15s", "&:hover": { boxShadow: "var(--shadow-card-md)", borderColor: "var(--color-hairline-cool)", transform: "translateY(-1px)" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "11px" }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "11px", flexShrink: 0, display: "grid", placeItems: "center", bgcolor: v.bg, color: v.color }}>
            <ReportIcon reportId={report.id} />
          </Box>
          <Typography sx={{ fontSize: "14.5px", fontWeight: 700, color: D.ink, letterSpacing: "-.01em", lineHeight: 1.25, fontFamily: "var(--font-display)" }}>
            {report.title}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "12.8px", color: D.ink2, lineHeight: 1.5, mb: "14px", flex: 1 }}>
          {report.description}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", mb: "13px" }}>
          <Box sx={{ display: "flex", gap: "5px", ml: "auto" }}>
            {report.formats.map((f) => (
              <Box key={f} sx={{ fontFamily: "monospace", fontSize: "9.5px", fontWeight: 600, letterSpacing: ".04em", color: D.ink3, bgcolor: D.surface3, border: `1px solid ${D.line2}`, px: "6px", py: "2px", borderRadius: "5px" }}>{f}</Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ mt: "auto" }}>
          {isCoiReport ? (
            <>
              <Button fullWidth onClick={() => handleGenerate(report.id)} disabled={coiLoading} sx={{ height: "38px", borderRadius: "10px", fontSize: "13px", fontWeight: 600, textTransform: "none", bgcolor: D.surface, color: D.primary, border: `1px solid ${D.line}`, display: "inline-flex", alignItems: "center", gap: "8px", "&:hover": { bgcolor: D.primary, color: "var(--color-on-primary)", borderColor: D.primary }, ...(coiLoading && { bgcolor: D.surface2, color: D.ink3, pointerEvents: "none" }) }}>
                {coiLoading ? <><CircularProgress size={14} sx={{ color: "inherit" }} /> Loading…</> : "Load report"}
              </Button>
              {coiError && <Alert severity="error" sx={{ mt: 1, borderRadius: "10px" }}>{coiError}</Alert>}
              {coiRows !== null && (coiRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No registrations found for this class.</Typography>
              ) : (
                <Box sx={{ overflowX: "auto", mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {["Student","Email","Agency","COI Document","Valid From","Valid To","Status","Uploaded"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {coiRows.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.studentName || "—"}</TableCell>
                          <TableCell>{row.studentEmail || "—"}</TableCell>
                          <TableCell>{row.agencyName || "—"}</TableCell>
                          <TableCell>{row.coiFilename ? (row.documentUrl ? <a href={row.documentUrl} target="_blank" rel="noreferrer">{row.coiFilename}</a> : row.coiFilename) : <Typography variant="caption" color="text.secondary">No COI</Typography>}</TableCell>
                          <TableCell>{row.validFrom || "—"}</TableCell>
                          <TableCell>{row.validTo || "No expiry"}</TableCell>
                          <TableCell>{row.coiStatus ? <CoiStatusChip status={row.coiStatus} /> : <Chip label="N/A" size="small" />}</TableCell>
                          <TableCell>{row.coiUploadedAt ? new Date(row.coiUploadedAt).toLocaleDateString() : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </>
          ) : (
            <Button fullWidth onClick={() => handleGenerate(report.id)} disabled={isGenerating} sx={{ height: "38px", borderRadius: "10px", fontSize: "13px", fontWeight: 600, textTransform: "none", display: "inline-flex", alignItems: "center", gap: "8px", ...(isDone ? { bgcolor: D.green50, color: D.green, border: `1px solid ${D.green100}`, pointerEvents: "none" } : isGenerating ? { bgcolor: D.surface2, color: D.ink3, border: `1px solid ${D.line}`, pointerEvents: "none" } : { bgcolor: D.surface, color: D.primary, border: `1px solid ${D.line}`, "&:hover": { bgcolor: D.primary, color: "var(--color-on-primary)", borderColor: D.primary } }) }}>
              {isDone ? "Ready · downloaded" : isGenerating ? <><CircularProgress size={14} sx={{ color: "inherit" }} /> Generating…</> : "Generate"}
            </Button>
          )}
        </Box>
      </Paper>
    );
  }

  // ── Attendance chart data ──────────────────────────────────────────────────
  const chartSessions = sessions
    .slice()
    .sort((a, b) => new Date(a.sessionDate || a.date || 0) - new Date(b.sessionDate || b.date || 0))
    .slice(0, 10);

  const maxPct = 100;

  const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return (
    <Box>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        gap: "12px", mb: "16px", flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, letterSpacing: "-.02em", color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>
            {course?.title} — Progress Report
          </Typography>
          <Box sx={{ fontSize: "13.5px", color: D.ink3, mt: "4px" }}>
            {course?.courseNumber && <>{course.courseNumber} · </>}
            As of {today} · {completedSessions.length} of {sessions.length} sessions completed
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <ActionBtn icon={<SvgIcon size={13}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></SvgIcon>}>
            Export CSV
          </ActionBtn>
          <ActionBtn icon={<SvgIcon size={13}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></SvgIcon>}>
            Export PDF
          </ActionBtn>
        </Box>
      </Box>

      {/* ── Summary rstat tiles ──────────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
        gap: "12px", mb: "20px" }}>
        <RstatTile
          value={avgAtt > 0 ? `${avgAtt}%` : "—"}
          label="Avg Attendance Rate"
          sub={avgAtt > 0 && avgAtt < 75 ? "↓ Below target" : avgAtt >= 75 ? "↑ On target" : undefined}
          subColor={avgAtt > 0 && avgAtt < 75 ? D.amber : D.green}
        />
        <RstatTile
          value={checklists.length > 0 ? `${skillComp}%` : "—"}
          label="Skill Completion Rate"
          sub={enrolledCount > 0 ? `${Math.round((skillComp / 100) * enrolledCount)} of ${enrolledCount} students` : undefined}
          subColor={D.ink3}
        />
        <RstatTile
          value={`${completedSessions.length}/${sessions.length}`}
          label="Sessions Completed"
          sub={futureSessions.length > 0 ? `${futureSessions.length} remaining` : "All done"}
          subColor={D.primary}
        />
        <RstatTile
          value={checklists.length > 0 && enrolledCount > 0
            ? (checklists.reduce((s, c) => s + (c.instructorCompletion || 0), 0) / checklists.length / 100 * (checklists.length)).toFixed(1)
            : "—"}
          label="Avg Sign-offs per Student"
          sub={checklists.length > 0 ? `of ${checklists.length} required` : undefined}
          subColor={D.green}
        />
      </Box>

      {/* ── Session Attendance chart ─────────────────────────────────────── */}
      <PanelCard
        icon={<SvgIcon><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></SvgIcon>}
        title="Session Attendance"
      >
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "8px", height: "120px", pt: "8px" }}>
          {chartSessions.map((s, i) => {
            const pct = s.checkedIn > 0
              ? Math.round((s.checkedIn / (s.totalStudents || enrolledCount || 1)) * 100)
              : null;
            const barH = pct != null ? Math.max(4, (pct / maxPct) * 100) : 4;
            const barColor = pct == null ? D.line : pct >= 80 ? D.green : D.amber;
            const label = formatDateShort(s.sessionDate || s.date);
            return (
              <Box key={i} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", flex: 1 }}>
                <Box sx={{ fontSize: "11px", fontWeight: 700, color: pct != null ? D.ink3 : D.ink4, textAlign: "center", minHeight: "14px" }}>
                  {pct != null ? `${pct}%` : "—"}
                </Box>
                <Box sx={{ width: "100%", height: `${barH}px`, bgcolor: barColor, borderRadius: "6px 6px 0 0",
                  transition: "height .3s", opacity: 0.9 }} />
                <Box sx={{ fontSize: "10px", fontWeight: 700, color: D.ink4, textAlign: "center", whiteSpace: "nowrap" }}>
                  {label || `S${i + 1}`}
                </Box>
              </Box>
            );
          })}
          {chartSessions.length === 0 && (
            <Box sx={{ flex: 1, textAlign: "center", color: D.ink4, fontSize: "13px", alignSelf: "center" }}>
              No sessions yet
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px", mt: "12px",
          borderTop: `1px solid ${D.line2}`, pt: "12px", flexWrap: "wrap" }}>
          {[
            { color: D.green, label: "≥ 80% attendance" },
            { color: D.amber, label: "< 80% attendance" },
            { color: D.line, label: "Upcoming" },
          ].map((l) => (
            <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: l.color }} />
              <Box sx={{ fontSize: "12px", fontWeight: 600, color: D.ink3 }}>{l.label}</Box>
            </Box>
          ))}
        </Box>
      </PanelCard>

      {/* ── Per-student breakdown ────────────────────────────────────────── */}
      <PanelCard
        iconBg={D.green50} iconColor={D.green}
        icon={<SvgIcon><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></SvgIcon>}
        title="Per-Student Breakdown"
        sx={{ mb: "20px" }}
        headerRight={
          <ActionBtn icon={<SvgIcon size={13}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></SvgIcon>}>
            Export
          </ActionBtn>
        }
      >
        {studentRows.length === 0 ? (
          <Box sx={{ textAlign: "center", color: D.ink4, fontSize: "13px", py: "24px" }}>
            No enrolled students
          </Box>
        ) : (
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <Box component="thead">
              <Box component="tr">
                {["Student","Sessions Attended","Attendance %","Status"].map((h) => (
                  <Box key={h} component="th" sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".07em",
                    textTransform: "uppercase", color: D.ink4, px: "12px", py: "9px",
                    textAlign: "left", borderBottom: `1px solid ${D.line2}`, whiteSpace: "nowrap",
                    "&:first-of-type": { pl: 0 } }}>
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {studentRows.map((r) => (
                <Box key={r.uid} component="tr" sx={{ borderBottom: `1px solid ${D.line2}`,
                  "&:last-child": { borderBottom: 0 },
                  "&:hover": { bgcolor: D.surface2 }, cursor: "default" }}>
                  <Box component="td" sx={{ px: "12px", py: "13px", pl: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <Ava initials={getInitials(r.name || "")} color={colorFor(r.name || "")} size={28} />
                      <Box sx={{ fontSize: "14px", fontWeight: 700, color: D.ink }}>{r.name}</Box>
                    </Box>
                  </Box>
                  <Box component="td" sx={{ px: "12px", py: "13px", fontSize: "13px", fontWeight: 700, color: D.ink }}>
                    {r.attended} / {r.completedSessions}
                  </Box>
                  <Box component="td" sx={{ px: "12px", py: "13px" }}>
                    <Box component="span" sx={{ fontSize: "13px", fontWeight: 800,
                      color: r.attPct >= 80 ? D.green : r.attPct >= 50 ? D.amber : r.attPct === 0 && r.completedSessions > 0 ? D.danger : D.ink3 }}>
                      {r.completedSessions > 0 ? `${r.attPct}%` : "—"}
                    </Box>
                  </Box>
                  <Box component="td" sx={{ px: "12px", py: "13px" }}>
                    <StatusPill status={r.status} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </PanelCard>

      {/* ── Academic Reports ─────────────────────────────────────────────── */}
      <Box sx={{ mt: "8px" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", mb: "20px", flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontSize: "18px", fontWeight: 800, letterSpacing: "-.02em", color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>
              Academic Reports
            </Typography>
            <Typography sx={{ fontSize: "13.5px", color: D.ink2, mt: "4px" }}>
              Generate and export course data as PDF or spreadsheet for this cohort.
            </Typography>
          </Box>
        </Box>

        {/* Full Course Export banner */}
        <Paper elevation={0} sx={{ display: "grid", gridTemplateColumns: { xs: "auto 1fr", sm: "auto 1fr auto" }, gap: "20px", alignItems: "center", background: `linear-gradient(135deg, ${D.primary700} 0%, ${D.primary} 60%, ${D.primary} 100%)`, borderRadius: "16px", p: "20px 22px", color: "var(--color-on-primary)", mb: "24px", boxShadow: D.shadowSm, position: "relative", overflow: "hidden", "&::after": { content: '""', position: "absolute", right: "-40px", top: "-40px", width: "220px", height: "220px", borderRadius: "50%", background: "radial-gradient(circle, var(--color-on-dark-faint), transparent 70%)" } }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "12px", flexShrink: 0, display: "grid", placeItems: "center", bgcolor: "var(--color-on-dark-faint)", color: "var(--color-on-primary)", border: "1px solid var(--color-on-dark-faint)", position: "relative", zIndex: 1 }}>
            <Box component="svg" width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><path d="M12 11v6"/><path d="m9 14 3-3 3 3"/>
            </Box>
          </Box>
          <Box sx={{ minWidth: 0, position: "relative", zIndex: 1 }}>
            <Typography sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--color-on-dark-muted)", mb: "4px" }}>Complete export</Typography>
            <Typography sx={{ fontSize: "18px", fontWeight: 800, letterSpacing: "-.01em", color: "var(--color-on-primary)", mb: "4px", fontFamily: "var(--font-display)" }}>Full Course Export</Typography>
            <Typography sx={{ fontSize: "13px", color: "var(--color-on-primary)" }}>Everything for this cohort — sessions, attendance, skill checklists, test results and student reports.</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0, position: "relative", zIndex: 1, gridColumn: { xs: "1 / -1", sm: "auto" } }}>
            <Button onClick={() => handleGenerate("full_export")} sx={{ height: "42px", px: "20px", borderRadius: "11px", border: 0, bgcolor: "var(--color-surface-canvas-light)", color: D.primary, fontSize: "13.5px", fontWeight: 700, textTransform: "none", display: "inline-flex", alignItems: "center", gap: "9px", "&:hover": { bgcolor: "color-mix(in srgb, var(--color-primary) 6%, transparent)", transform: "translateY(-1px)" } }}>
              Export everything
            </Button>
          </Box>
        </Paper>

        {/* Report sections */}
        {SECTIONS.map((section) => {
          const items = section.reportIds.map((rid) => REPORTS.find((r) => r.id === rid)).filter(Boolean);
          return (
            <Box key={section.label} sx={{ mb: "26px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "14px" }}>
                <Typography sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: D.ink3 }}>
                  {section.label}
                </Typography>
                <Box sx={{ flex: 1, height: "1px", bgcolor: D.line }} />
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: D.ink4 }}>
                  {items.length} report{items.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: "14px" }}>
                {items.map((report) => <ReportCard key={report.id} report={report} />)}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function ActionBtn({ children, icon, onClick }) {
  return (
    <Box component="button" onClick={onClick} sx={{
      height: 32, px: "13px", borderRadius: "9px",
      border: `1.5px solid ${D.line}`, bgcolor: D.surface2,
      fontFamily: "inherit", fontSize: "12.5px", fontWeight: 700, color: D.ink2,
      cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px",
      transition: "all .12s", whiteSpace: "nowrap",
      "&:hover": { bgcolor: D.surface3, borderColor: "var(--color-hairline-cool)", color: D.ink },
    }}>
      {icon}{children}
    </Box>
  );
}
